/**
 * User menu dropdown component for authenticated users.
 *
 * Displays user profile information and sign-out option.
 */

import React, { useState, useRef } from 'react';

import { useAuth } from '../../hooks/useAuth';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useTranslation } from '../../hooks/useTranslation';

/**
 * User menu component.
 *
 * @returns UserMenu component
 */
export const UserMenu: React.FC = () => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [photoError, setPhotoError] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside.
  useClickOutside(menuRef, () => setIsOpen(false));

  if (!user) {
    return null;
  }

  /**
   * Handle sign out.
   */
  const handleSignOut = async (): Promise<void> => {
    try {
      await signOut();
      setIsOpen(false);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  /**
   * Get user display name or email.
   */
  const displayName = user.displayName || user.email || t('auth.user');

  /**
   * Get user initials for avatar.
   */
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className="relative flex-shrink-0" ref={menuRef}>
      {/* User avatar button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/10 transition-colors duration-200 min-h-[44px] min-w-[44px] whitespace-nowrap"
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {user.photoURL && !photoError ? (
          <img
            src={user.photoURL}
            alt=""
            className="w-9 h-9 sm:w-8 sm:h-8 rounded-full flex-shrink-0"
            onError={() => setPhotoError(true)}
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-9 h-9 sm:w-8 sm:h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
            {initials}
          </div>
        )}
        <span className="text-sm font-medium text-white/80 hidden md:inline whitespace-nowrap">
          {displayName}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute left-0 sm:left-auto sm:right-0 mt-2 min-w-[200px] w-max max-w-[280px] bg-white dark:bg-secondary-900 rounded-lg shadow-xl border border-gray-200 dark:border-secondary-600 py-2 z-50">
          {/* User info */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-secondary-600">
            <p className="text-sm font-medium text-gray-900 dark:text-white">{displayName}</p>
            {user.email && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                {user.email}
              </p>
            )}
          </div>

          {/* Menu items */}
          <div className="py-1">
            <button
              onClick={handleSignOut}
              className="w-full text-left px-4 py-3 min-h-[44px] text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-secondary-700 transition-colors duration-200 flex items-center gap-3 whitespace-nowrap"
              type="button"
            >
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
